import { useAuth } from '@/app/_auth/auth-hooks'
import { AuthStep } from '@/app/_auth/auth-step-navigator'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { toast } from '@/components/ui/toaster'
import { useDeactivateAccountMutation } from '@/hooks/api/use-deactivate-account-mutation'
import { useQueryClient } from '@tanstack/react-query'
import { T, useTranslate } from '@tolgee/react'

export function DeactivateDialog({ children }: Readonly<{ children: React.ReactNode }>) {
  const { t } = useTranslate()
  const { setStep, setApiBaseUrl, setUser } = useAuth()
  const queryClient = useQueryClient()
  const { mutate, isPending } = useDeactivateAccountMutation({
    onSuccess: async () => {
      // Clear all cached data to force fresh fetch
      queryClient.removeQueries()
      // Clear auth context state to ensure clean refetch
      setUser(null)
      setApiBaseUrl(null)
      // Redirect to session data step which will refetch everything
      setStep(AuthStep.GetSessionData)
    },
    onError: () => toast.error(t('account.deactivate.toasts.error')),
  })

  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogPortal>
        <DialogContent onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>
              <T keyName='account.deactivate.dialog.title' />
            </DialogTitle>
            <DialogDescription>
              <T keyName='account.deactivate.dialog.body' />
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button
                disabled={isPending}
                title={t('shared.cancelDialog.a11y')}
                variant='mango-outline'
              >
                <T keyName='shared.cancelDialog.title' />
              </Button>
            </DialogClose>
            <Button
              onClick={() => mutate()}
              loading={isPending}
              title={t('account.deactivate.dialog.button.a11y')}
            >
              <T keyName='account.deactivate.dialog.button.title' />
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}
